/**
 * The theme SCHEMAS and normalization: strict Zod parsing, canonical form and the reset shape.
 *
 * theme-customization A1, split out of `theme.ts` at the head of A2a (behaviour-preserving —
 * every export below is re-exported by `theme.js`, so no importer changed). Everything here
 * turns untrusted input into a canonical `ThemeOverrides`; resolving and contrast-validating
 * that record is `theme.ts`'s job.
 */

import { z } from 'zod';
import { HEX_COLOUR_PATTERN, normalizeHexColour } from './contrast.js';
import {
  THEME_ROLES,
  THEME_ROLE_IDS,
  type RoleOverrides,
  type ThemeMode,
  type ThemeOverrides,
  type ThemeRoleId,
} from './theme-catalogue.js';

// ─── Strict schemas ──────────────────────────────────────────────────────────

/**
 * Six-digit hex only. An incomplete hex belongs in FORM INPUT STATE and must never reach a
 * resolved preview or a request, so there is no lenient variant here on purpose.
 */
export const hexColourSchema = z
  .string()
  .regex(HEX_COLOUR_PATTERN, 'must be a six-digit hex colour such as #1A1A1A');

export const themeRoleIdSchema = z.enum(
  THEME_ROLE_IDS as unknown as [ThemeRoleId, ...ThemeRoleId[]],
);

/**
 * Strict at every object level: unknown role IDs, unknown fields, arrays and nulls all fail.
 * (Zod's object parser rejects arrays and null before it looks at any key.)
 */
export const roleOverridesSchema = z
  .object(
    Object.fromEntries(THEME_ROLE_IDS.map((id) => [id, hexColourSchema.optional()])) as Record<
      ThemeRoleId,
      z.ZodOptional<typeof hexColourSchema>
    >,
  )
  .strict();

export const themeOverridesSchema = z
  .object({ light: roleOverridesSchema, dark: roleOverridesSchema })
  .strict();

/** PostgreSQL `integer` upper bound. The save path refuses to increment past it. */
export const MAX_THEME_REVISION = 2147483647;

export const themeRevisionSchema = z.number().int().min(0).max(MAX_THEME_REVISION);

export const themeSaveSchema = z
  .object({ overrides: themeOverridesSchema, revision: themeRevisionSchema })
  .strict();

export const themePreferenceSchema = z.enum(['SYSTEM', 'LIGHT', 'DARK']);

export const themeSnapshotSchema = z
  .object({
    ownerUserId: z.string().min(1),
    preference: themePreferenceSchema,
    overrides: themeOverridesSchema,
    revision: themeRevisionSchema,
    supportedModes: z.array(themePreferenceSchema).min(1),
  })
  .strict();

/**
 * Mixed-version protocol negotiation. Delivery A accepts the query and always answers with
 * LIGHT capabilities; delivery B answers version 2 with the complete set. This is not an
 * environment flag and not an authorization input.
 */
export const themeClientQuerySchema = z
  .object({ clientVersion: z.enum(['1', '2']).optional().default('1') })
  .strict();

export type ThemeClientVersion = z.infer<typeof themeClientQuerySchema>['clientVersion'];

// ─── Normalization ───────────────────────────────────────────────────────────

/**
 * Uppercase every value and emit keys in catalogue order, so two records that mean the same
 * thing serialize to the same bytes. Unknown keys cannot reach this function — the schema
 * rejects them first — but an unknown key is dropped rather than trusted, defensively.
 */
export function canonicalizeRoleOverrides(record: RoleOverrides): RoleOverrides {
  const out: RoleOverrides = {};
  for (const id of THEME_ROLE_IDS) {
    const value = record[id];
    if (value === undefined) continue;
    const hex = normalizeHexColour(value);
    if (hex === null) throw new Error(`${id} is not a six-digit hex colour`);
    out[id] = hex;
  }
  return out;
}

/**
 * Canonicalize AND drop every entry equal to that mode's stock — "Normalize case and remove
 * entries equal to mode stock. Empty records mean reset."
 *
 * Only ever applied to a SUPPORTED mode. The unsupported, preserved dark arm keeps its
 * default-valued keys (the plan forbids stripping them), so it gets `canonicalizeRoleOverrides`.
 */
export function normalizeRoleOverrides(mode: ThemeMode, record: RoleOverrides): RoleOverrides {
  const canonical = canonicalizeRoleOverrides(record);
  const out: RoleOverrides = {};
  for (const id of THEME_ROLE_IDS) {
    const value = canonical[id];
    if (value === undefined) continue;
    const role = THEME_ROLES.find((candidate) => candidate.id === id);
    if (role && role.stock[mode] === value) continue;
    out[id] = value;
  }
  return out;
}

/** Stable byte form for the "byte-equivalent after canonical key/case normalization" rule. */
export function roleOverridesFingerprint(record: RoleOverrides): string {
  return JSON.stringify(canonicalizeRoleOverrides(record));
}

export function isRoleOverridesEmpty(record: RoleOverrides): boolean {
  return Object.keys(canonicalizeRoleOverrides(record)).length === 0;
}

export const EMPTY_THEME_OVERRIDES: ThemeOverrides = Object.freeze({
  light: Object.freeze({}) as RoleOverrides,
  dark: Object.freeze({}) as RoleOverrides,
});
